import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Person,
  Email,
  Phone,
  Business,
  LocationOn,
  Badge,
  VpnKey,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../services/userService';
import { getApiCollection, getApiData, getProp } from '../utils/apiResponse';
import { styles } from '../utils/styles';

// Map SKU part numbers to friendly names
const getFriendlyLicenseName = (skuPartNumber: string): string => {
  const licenseNames: { [key: string]: string } = {
    SPE_E3: 'Microsoft 365 E3',
    SPE_E5: 'Microsoft 365 E5',
    SPE_F1: 'Microsoft 365 F1',
    SPE_F3: 'Microsoft 365 F3',
    O365_BUSINESS_ESSENTIALS: 'Microsoft 365 Business Basic',
    O365_BUSINESS_PREMIUM: 'Microsoft 365 Business Standard',
    SPB: 'Microsoft 365 Business Premium',
    ENTERPRISEPACK: 'Office 365 E3',
    ENTERPRISEPREMIUM: 'Office 365 E5',
    DESKLESSPACK: 'Office 365 F3',
    STANDARDPACK: 'Office 365 E1',
    EXCHANGESTANDARD: 'Exchange Online Plan 1',
    EXCHANGEENTERPRISE: 'Exchange Online Plan 2',
    SHAREPOINTSTANDARD: 'SharePoint Online Plan 1',
    SHAREPOINTENTERPRISE: 'SharePoint Online Plan 2',
    VISIOCLIENT: 'Visio Plan 2',
    VISIOONLINE_PLAN1: 'Visio Plan 1',
    PROJECTPREMIUM: 'Project Plan 5',
    PROJECTPROFESSIONAL: 'Project Plan 3',
    POWER_BI_PRO: 'Power BI Pro',
    EMSPREMIUM: 'Enterprise Mobility + Security E5',
    EMS: 'Enterprise Mobility + Security E3',
    AAD_PREMIUM: 'Azure Active Directory Premium P1',
    AAD_PREMIUM_P2: 'Azure Active Directory Premium P2',
  };
  return licenseNames[skuPartNumber] || skuPartNumber;
};

export const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [licensesError, setLicensesError] = useState<string | null>(null);

  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLicensesError(null);
      const response = await userService.getUserById(id!);
      const userData = getApiData<any>(response, 'loading user details');
      setUser(userData);

      try {
        const licensesResponse = await userService.getUserLicenses(id!);
        const licensesData = getApiCollection<any>(licensesResponse, 'loading user licenses');
        setLicenses(licensesData);
      } catch (licenseErr: any) {
        setLicenses([]);
        setLicensesError(licenseErr.message || 'Failed to load user licenses');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void loadUser();
    }
  }, [id, loadUser]);

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="error">{error || 'User not found'}</Alert>
      </Box>
    );
  }

  const displayName = getProp(user, 'displayName');
  const userPrincipalName = getProp(user, 'userPrincipalName');
  const mail = getProp(user, 'mail');
  const givenName = getProp(user, 'givenName');
  const surname = getProp(user, 'surname');
  const jobTitle = getProp(user, 'jobTitle');
  const department = getProp(user, 'department');
  const officeLocation = getProp(user, 'officeLocation');
  const mobilePhone = getProp(user, 'mobilePhone');
  const businessPhones = getProp(user, 'businessPhones') || [];
  const accountEnabled = getProp(user, 'accountEnabled');
  const createdDateTime = getProp(user, 'createdDateTime');
  const userType = getProp(user, 'userType');
  const isGuest = userType === 'Guest' || userPrincipalName?.includes('#EXT#');

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/users')} sx={{ mb: 3 }}>
        Back to Users
      </Button>

      <Box sx={styles.pageHeader}>
        <Box sx={styles.flexCenter}>
          <Person sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {userPrincipalName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" color="error" startIcon={<Delete />}>
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Status and Basic Info */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={accountEnabled ? 'Enabled' : 'Disabled'}
                color={accountEnabled ? 'success' : 'default'}
              />
              {isGuest && <Chip label="Guest" color="info" />}
              <Chip
                label={`${licenses.length} License${licenses.length !== 1 ? 's' : ''}`}
                color={licenses.length > 0 ? 'primary' : 'default'}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <Badge sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Display Name" secondary={displayName || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <Person sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Given Name" secondary={givenName || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <Person sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Surname" secondary={surname || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <VpnKey sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText
                    primary="User Principal Name"
                    secondary={userPrincipalName || '-'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <Email sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Email" secondary={mail || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Mobile Phone" secondary={mobilePhone || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <Phone sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText
                    primary="Business Phones"
                    secondary={businessPhones.length > 0 ? businessPhones.join(', ') : '-'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Work Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Work Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <Badge sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Job Title" secondary={jobTitle || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <Business sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Department" secondary={department || '-'} />
                </ListItem>
                <ListItem disableGutters>
                  <LocationOn sx={{ mr: 2, color: 'text.secondary' }} />
                  <ListItemText primary="Office Location" secondary={officeLocation || '-'} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Account Status"
                    secondary={accountEnabled ? 'Enabled' : 'Disabled'}
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="User Type" secondary={userType || 'Member'} />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText
                    primary="Created Date"
                    secondary={
                      createdDateTime ? new Date(createdDateTime).toLocaleDateString() : '-'
                    }
                  />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="User ID" secondary={getProp(user, 'id') || '-'} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Assigned Licenses */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Assigned Licenses ({licenses.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {licensesError ? (
                <Alert severity="warning">{licensesError}</Alert>
              ) : licenses.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {licenses.map((license: any, index: number) => {
                    const skuPartNumber = getProp(license, 'skuPartNumber');
                    const friendlyName = getFriendlyLicenseName(skuPartNumber);
                    return (
                      <Chip key={index} label={friendlyName} color="primary" variant="outlined" />
                    );
                  })}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No licenses are currently assigned to this user.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
